import React from 'react';

/**
 * IconButton Component - Atomic Level
 * 
 * A button component specifically for icon-only actions
 * 
 * Features:
 * - Multiple sizes
 * - Multiple variants
 * - Accessible with aria-label
 * - Badge support
 */

export type IconButtonVariant = 'default' | 'primary' | 'ghost' | 'outline';
export type IconButtonSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<IconButtonVariant, string> = {
  default: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm',
  primary: 'bg-[#EAB308] hover:bg-[#CA8A04] text-white shadow-md',
  ghost: 'bg-transparent hover:bg-gray-100 text-gray-600',
  outline: 'bg-transparent border-2 border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308] hover:text-white',
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: IconButtonVariant;
  /** Button size */
  size?: IconButtonSize;
  /** Icon element */
  icon: React.ReactNode;
  /** Badge element to show (e.g., notification count) */
  badge?: React.ReactNode;
  /** ARIA label (required for accessibility) */
  ariaLabel: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className = '',
      variant = 'default',
      size = 'md',
      icon,
      badge,
      ariaLabel,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'relative inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#EAB308] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
    
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        aria-label={ariaLabel}
        className={buttonClasses}
        {...props}
      >
        {icon}
        {badge && (
          <span className="absolute -top-1 -right-1">
            {badge}
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;

